import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tag, X, Loader2, CheckCircle2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

export type CouponContext = "videos" | "subscriptions" | "all_access" | "tripwire";

export interface AppliedCoupon {
  code: string;
  coupon_id: string;
  discount_amount: number;
  final_amount: number;
  discount_type: "percentage" | "fixed_amount";
  discount_value: number;
}

interface Props {
  context: CouponContext;
  amount: number;
  currency?: string;
  applied: AppliedCoupon | null;
  onApply: (c: AppliedCoupon) => void;
  onRemove: () => void;
  className?: string;
}

export default function CouponInput({ context, amount, applied, onApply, onRemove, className }: Props) {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!code.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const { data, error: fnErr } = await supabase.functions.invoke("validate-coupon", {
        body: { code: code.trim(), context, amount },
      });
      if (fnErr) throw new Error(fnErr.message);
      if (!data?.valid) {
        setError(data?.message || "Invalid coupon");
        return;
      }
      onApply({
        code: data.code,
        coupon_id: data.coupon_id,
        discount_amount: data.discount_amount,
        final_amount: data.final_amount,
        discount_type: data.discount_type,
        discount_value: data.discount_value,
      });
      setCode("");
    } catch (e: any) {
      setError(e.message || "Could not validate coupon");
    } finally {
      setLoading(false);
    }
  };

  if (applied) {
    return (
      <div className={`flex items-center justify-between rounded-md border border-emerald-300 bg-emerald-50 dark:bg-emerald-950/20 px-3 py-2 ${className || ""}`}>
        <div className="flex items-center gap-2 text-sm">
          <CheckCircle2 className="h-4 w-4 text-emerald-600" />
          <span className="font-semibold">{applied.code}</span>
          <span className="text-muted-foreground">
            −{applied.discount_type === "percentage" ? `${applied.discount_value}%` : `$${applied.discount_value}`}
          </span>
        </div>
        <Button variant="ghost" size="sm" onClick={onRemove} className="h-7 px-2">
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>
    );
  }

  return (
    <div className={className}>
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Tag className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="Coupon code"
            className="pl-8 h-9 text-sm uppercase"
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), submit())}
            disabled={loading}
          />
        </div>
        <Button type="button" variant="outline" size="sm" onClick={submit} disabled={loading || !code.trim()}>
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Apply"}
        </Button>
      </div>
      {error && <p className="mt-1 text-xs text-destructive">{error}</p>}
    </div>
  );
}
